import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { X, FileText, Image, File, Upload } from 'lucide-react';
import { formatFileSize } from './helpers';

// Drag & Drop Zone Component
export const DragDropZone = ({ files, setFiles, setError, maxFiles = 10, maxSize = 25 * 1024 * 1024 }) => {
  const allowedTypes = {
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/*': ['.txt', '.csv'],
    'audio/*': ['.mp3', '.wav'],
    'video/*': ['.mp4']
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    rejectedFiles.forEach(({ file, errors }) => {
      errors.forEach(e => {
        if (e.code === 'file-too-large') {
          setError(`Ficheiro "${file.name}" demasiado grande (max ${formatFileSize(maxSize)})`);
        } else if (e.code === 'file-invalid-type') {
          setError(`Tipo de ficheiro não permitido: ${file.name}`);
        } else if (e.code === 'too-many-files') {
          setError(`Máximo ${maxFiles} ficheiros`);
        }
      });
    });

    // Add accepted files
    if (files.length + acceptedFiles.length > maxFiles) {
      setError(`Máximo ${maxFiles} ficheiros por denúncia`);
      return;
    }

    setFiles(prev => [...prev, ...acceptedFiles].slice(0, maxFiles));
  }, [files, maxFiles, maxSize, setFiles, setError]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    maxFiles: maxFiles - files.length,
    maxSize,
    accept: allowedTypes,
    disabled: files.length >= maxFiles
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${isDragActive && !isDragReject ? 'border-blue-500 bg-blue-50' : ''}
          ${isDragReject ? 'border-red-500 bg-red-50' : ''}
          ${!isDragActive ? 'border-slate-300 hover:border-slate-400 bg-slate-50' : ''}
          ${files.length >= maxFiles ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        data-testid="drag-drop-zone"
      >
        <input {...getInputProps()} data-testid="file-input" />

        <div className="flex flex-col items-center gap-2">
          <Upload className={`w-8 h-8 ${isDragActive ? 'text-blue-500' : 'text-slate-400'}`} />

          {isDragActive ? (
            <p className="text-blue-600 font-medium">Largue os ficheiros aqui...</p>
          ) : (
            <>
              <p className="text-slate-600 font-medium">
                Arraste ficheiros ou clique para selecionar
              </p>
              <p className="text-sm text-slate-400">
                PDF, DOC, XLS, Imagens, Áudio, Vídeo (max {maxFiles} ficheiros, {formatFileSize(maxSize)} cada)
              </p>
            </>
          )}
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-slate-500">{files.length} de {maxFiles} ficheiros selecionados</p>
          {files.map((file, index) => (
            <div key={`${file.name}-${file.size}-${file.lastModified}`} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center gap-3 min-w-0">
                {/* Preview or Icon */}
                {file.type?.startsWith('image/') ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="w-10 h-10 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                    {file.type?.includes('pdf') ? (
                      <FileText className="w-5 h-5 text-red-500" />
                    ) : file.type?.startsWith('image/') ? (
                      <Image className="w-5 h-5 text-blue-500" />
                    ) : (
                      <File className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                )}

                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                  <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => removeFile(index)}
                className="p-1 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                data-testid={`remove-file-${index}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
